import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from "react";
import { Form } from "react-formio";
import axios from "axios";
import "../../components/css/FormioOverrides.css";
import "../../components/css/ParticipantEditPersonalSettings.css";
import { snapshotFormioInstanceData } from "../../utils/snapshotFormioInstanceData";
import {
  clearFormioValidationState,
  collectFormioValidationItems,
  forceFormioShowValidationErrors,
} from "../../utils/formioSubmissionNormalize";
import {
  formSchemaHasCaddieSelect,
  fetchCaddieOptions,
  buildCaddiesById,
  injectCaddieSelectValues,
  ensureCaddieDatagridLayoutFromSchema,
  applyCaddieOnFormChange,
  syncCaddieFieldsForFormio,
  reinjectCaddieOptionsOnFormio,
  prefillCaddieDatagridRows,
  ensureDatagridRowActions,
  normalizeDataGridForFormioRender,
  prepareCaddieGridDisplayData,
  resolveCaddieSubmitPayload,
  finalizeCaddieGridForSubmit,
  isDefaultRadioFormChange,
  mergeKnownCaddiesIntoOptionsList,
  pushFormioSubmissionData,
  attachCaddieEmailAutoFill,
} from "../../utils/caddieFormioUtils";
import { USER_PORTAL_FORMIO_OPTIONS } from "../../utils/formioFormOptions";
import {
  collectFormDataKeys,
  ensureDatagridCellLabels,
  overrideSubmitSettings,
  sanitizeFormioSelectDefaults,
  sanitizeSelectSubmissionValues,
} from "../../utils/formSchemaUtils";
import {
  prepareFormSchemaWithInvitationSelects,
  formSchemaHasInvitationEntitlementLayout,
  stampPersonalSettingsRadioLayoutClasses,
} from "../../utils/invitationTypeFormioUtils";
import { formNameToCssClass } from "../../utils/formioFormNameClass";
import { refreshFormioPanelBadges } from "../../utils/formioProgressBadges";
import { calculateVisibleFieldProgress } from "../../utils/formVisibleProgress";
import {
  ACCREDITATION_FORM_ID,
  configureAccreditationFormFileUploads,
} from "../../constants/accreditationUploads";

/* ---------------- Utilities ---------------- */
const safeForEachRow = (rows, cb) => {
  if (!rows || !Array.isArray(rows)) return;
  rows.forEach((row) => {
    if (Array.isArray(row)) row.forEach((cell) => cb(cell));
    else if (row && typeof row === "object") cb(row);
  });
};

const ensureMatchingLabelIds = (components) => {
  if (!Array.isArray(components)) return;
  const traverse = (comps) => {
    if (!comps?.length) return;
    comps.forEach((component) => {
      if (component.key && component.input && !component.id) component.id = component.key;
      if (component.label && component.key && !component.labelFor) component.labelFor = component.key;
      if (component.components) traverse(component.components);
      if (component.columns) component.columns.forEach((col) => traverse(col.components || []));
      if (component.rows) safeForEachRow(component.rows, (cell) => cell?.components && traverse(cell.components));
      if (component.datagrid?.components) traverse(component.datagrid.components);
      if (component.editgrid?.components) traverse(component.editgrid.components);
      if (component.tabs) component.tabs.forEach((tab) => traverse(tab.components || []));
    });
  };
  traverse(components);
};

/** Same traversal as UserFormView + tabs (panels, columns, rows, datagrid, editgrid, tabs) */
const traverseComponents = (components, handler) => {
  if (!components || !Array.isArray(components)) return;
  components.forEach((component) => {
    handler(component);
    if (component.components) traverseComponents(component.components, handler);
    if (component.columns) component.columns.forEach((col) => traverseComponents(col.components || [], handler));
    if (component.rows) safeForEachRow(component.rows, (cell) => cell?.components && traverseComponents(cell.components, handler));
    if (component.datagrid?.components) traverseComponents(component.datagrid.components, handler);
    if (component.editgrid?.components) traverseComponents(component.editgrid.components, handler);
    if (component.tabs) component.tabs.forEach((tab) => traverseComponents(tab.components || [], handler));
  });
};

/**
 * Same as UserFormView: do not change validation — only remove trailing * from labels so UI matches user portal.
 * Form.io required behaviour (errors on fill/clear) stays intact.
 */
const stripRequiredUI = (components) => {
  traverseComponents(components, (c) => {
    if (!c) return;
    if (c.label) {
      c.label = c.label.replace(/\*+$/, "").trim();
    }
  });
};

/** Safe calculateValue for fullPhoneNumber etc.: phoneGroup may be missing or use country.dial_code / phoneNumber1. */
const SAFE_PHONE_CALCULATE =
  "var pg = data.phoneGroup; if (!pg) return ''; " +
  "var code = (pg.country && typeof pg.country === 'object' && pg.country.dial_code) ? pg.country.dial_code : (pg.countryCode || ''); " +
  "var digits = pg.phoneNumberDigits || pg.phoneNumber1 || pg.phoneNumber || ''; " +
  "return (code && digits) ? code + digits : '';";

const ensureSafePhoneGroupCalculate = (components) => {
  if (!Array.isArray(components)) return;
  traverseComponents(components, (comp) => {
    const calc = (comp.calculateValue || "").trim();
    if (
      calc &&
      calc.includes("phoneGroup.countryCode") &&
      (comp.key === "fullPhoneNumber" ||
        comp.key === "fullPhoneNumber1" ||
        comp.key === "professionalPhoneNumber" ||
        calc.includes("phoneNumberDigits"))
    ) {
      comp.calculateValue = SAFE_PHONE_CALCULATE;
    }
  });
};

/* ---------------- Main Component ---------------- */
const FormioTabRenderer = ({
  formId,
  participantId,
  submission,
  submissionVersion = 0,
  onDataChange,
  API_URL,
  /** Parent ref always holding latest Form.io submission.data. */
  liveDataRef,
  /** Parent assigns `ref.current = () => void`; call before save to snapshot live Webform data. */
  flushLiveFormDataRef,
  /** Parent ref — current Form.io webform instance (for save-time caddie radio read). */
  formioWebformRef,
  /** Tab switch: quiet save without full Form.io submit. */
  triggerSaveRef,
  /** Footer Submit: runs formio.submit() (validate + onSubmit). */
  triggerFormSubmitRef,
  /** Parent assigns `ref.current = () => void`; clears Form.io validation alerts after successful save. */
  clearFormErrorsRef,
  /** Called with submission.data when Form.io validates successfully — parent does the actual API save. */
  onFormSave,
  /** Called with { text, fieldKey }[] when validation fails — parent shows top summary only. */
  onValidationErrors,
  /** Notifies parent when schema is loaded (for save-time field filtering). */
  onFormSchemaLoad,
  /** Optional parent ref — receives the CRM caddie Map for save-time normalization. */
  sharedCaddiesByIdRef,
  /** Number of skeleton panel cards to show while loading. Defaults to 2. */
  skeletonPanels = 2,
  /** Pre-fetched schema from parent cache — skips network fetch when provided. */
  initialSchema = null,
  /** form_name for cached schema (invitation-type prep). */
  initialFormName = "",
  /** Optional Form.io option overrides merged over USER_PORTAL_FORMIO_OPTIONS. */
  formOptions = null,
  /** When true, add user-form--show-errors on the form container (same as user portal). */
  showInlineErrors = false,
  /**
   * When true, paint panel progress rings from live field counts
   * (same calculateVisibleFieldProgress as user portal). Off for EMS Participant Edit.
   */
  showProgressBadges = false,
  /** Optional: ({ filled, total, percentage }) when progress is recomputed. */
  onProgressStats = null,
}) => {
  const [formSchema, setFormSchema] = useState(() => initialSchema?.components ? initialSchema : null);
  const [formNameClass, setFormNameClass] = useState("");
  const [isInvitationTypesForm, setIsInvitationTypesForm] = useState(false);
  const [loading, setLoading] = useState(!initialSchema?.components);
  const [error, setError] = useState(null);
  const lastEmittedDataRef = useRef("");
  const reactFormRef = useRef(null);
  const caddiesByIdRef = useRef(new Map());
  const caddiesListRef = useRef([]);
  const formSchemaRef = useRef(formSchema);
  const showProgressBadgesRef = useRef(showProgressBadges);
  const onProgressStatsRef = useRef(onProgressStats);
  /** Resolves triggerSaveRef promise after onFormSave completes or validation fails. */
  const pendingSaveResolveRef = useRef(null);
  /** True while save was started by tab switch (parent triggerSaveRef), not the form Submit button. */
  const saveViaTabSwitchRef = useRef(false);
  /** Debounce timer — Form.io fires onError once per panel; we only surface one summary. */
  const errorDebounceRef = useRef(null);

  useEffect(() => {
    formSchemaRef.current = formSchema;
  }, [formSchema]);

  useEffect(() => {
    showProgressBadgesRef.current = showProgressBadges;
  }, [showProgressBadges]);

  useEffect(() => {
    onProgressStatsRef.current = onProgressStats;
  }, [onProgressStats]);

  const paintProgressBadges = useCallback((formio, data) => {
    if (!formio || !formSchemaRef.current?.components) {
      return;
    }
    const components = formSchemaRef.current.components;
    let result = null;
    if (showProgressBadgesRef.current) {
      result = refreshFormioPanelBadges(formio, data || {}, components);
    } else {
      result = calculateVisibleFieldProgress(data || {}, components, formio);
    }
    if (result && typeof onProgressStatsRef.current === "function") {
      onProgressStatsRef.current({
        filled: result.filled,
        total: result.total,
        percentage: result.percentage,
      });
    }
  }, []);
  /** Accumulated errors across multiple onError firings for the same submit. */
  const pendingErrorItemsRef = useRef([]);

  const cleanFormId = (formId || "").trim();
  const hasCaddieSelectRef = useRef(false);
  /** Best caddie snapshot from first panel submit with a live default radio. */
  const lastCaddieSubmitPayloadRef = useRef(null);

  const publishCaddiesById = useCallback(
    (map) => {
      if (sharedCaddiesByIdRef) sharedCaddiesByIdRef.current = map;
    },
    [sharedCaddiesByIdRef]
  );

  /* ───────── Fetch / prepare schema ───────── */
  useEffect(() => {
    if (!cleanFormId) {
      setFormSchema(null);
      setFormNameClass("");
      setIsInvitationTypesForm(false);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const applyPreparedSchema = async (rawSchema, formName = "") => {
      // Always clone — never mutate cached parent schemas in place.
      let schema = JSON.parse(JSON.stringify(rawSchema));
      if (!schema?.components) throw new Error("Invalid schema");

      // Invitation selects are optional enrichment — never block form render.
      try {
        const prepared = await prepareFormSchemaWithInvitationSelects(
          schema,
          API_URL,
          formName
        );
        schema = prepared.schema;
        if (!isMounted) return null;
        setIsInvitationTypesForm(
          Boolean(prepared.hasInvitationFormSelects) ||
            formSchemaHasInvitationEntitlementLayout(schema.components)
        );
      } catch (err) {
        console.warn("Invitation select prep failed — using base schema:", err);
        if (!isMounted) return null;
        setIsInvitationTypesForm(
          formSchemaHasInvitationEntitlementLayout(schema.components)
        );
      }

      stampPersonalSettingsRadioLayoutClasses(schema.components);
      ensureMatchingLabelIds(schema.components);
      stripRequiredUI(schema.components);
      overrideSubmitSettings(schema.components);
      ensureDatagridRowActions(schema.components);
      ensureSafePhoneGroupCalculate(schema.components);
      sanitizeFormioSelectDefaults(schema.components);

      if (
        String(cleanFormId) === "68e784018e1384b2c283c3bb" ||
        String(cleanFormId) === "68e785cb8e1384b2c283c3c5" ||
        String(cleanFormId) === "68e781408e1384b2c283c3af"
      ) {
        ensureDatagridCellLabels(schema.components);
      }

      if (String(cleanFormId) === ACCREDITATION_FORM_ID) {
        configureAccreditationFormFileUploads(schema.components, API_URL);
      }

      hasCaddieSelectRef.current = formSchemaHasCaddieSelect(schema.components);
      if (hasCaddieSelectRef.current) {
        ensureCaddieDatagridLayoutFromSchema(schema.components);
        const submissionData = submission?.data
          ? JSON.parse(JSON.stringify(submission.data))
          : {};
        // Caddie options are optional — form must still load if CRM list fails.
        try {
          const caddies = await fetchCaddieOptions(API_URL);
          if (!isMounted) return null;
          const mergedCaddies = mergeKnownCaddiesIntoOptionsList(
            caddies,
            submissionData
          );
          caddiesListRef.current = mergedCaddies;
          caddiesByIdRef.current = buildCaddiesById(mergedCaddies);
          publishCaddiesById(caddiesByIdRef.current);
          injectCaddieSelectValues(schema.components, mergedCaddies, submissionData);
        } catch (err) {
          console.warn("Caddie options failed — continuing without:", err);
          if (!isMounted) return null;
          caddiesListRef.current = [];
          caddiesByIdRef.current = new Map();
          publishCaddiesById(caddiesByIdRef.current);
        }
      } else {
        caddiesListRef.current = [];
        caddiesByIdRef.current = new Map();
        publishCaddiesById(caddiesByIdRef.current);
      }

      return { schema, formName };
    };

    const loadSchema = async () => {
      const hasCachedSchema = Boolean(initialSchema?.components);
      try {
        // Cached schema already painted — prep in place without skeleton flash.
        if (!hasCachedSchema) setLoading(true);
        setError(null);

        let rawSchema = null;
        let formName = "";

        if (hasCachedSchema) {
          rawSchema = initialSchema;
          formName = initialFormName || "";
        } else {
          const schemaRes = await axios.get(`${API_URL}/ems/forms/${cleanFormId}`);
          rawSchema = schemaRes.data?.schema;
          formName = schemaRes.data?.form_name || "";
        }

        const prepared = await applyPreparedSchema(rawSchema, formName);
        if (!isMounted || !prepared) return;

        setFormSchema(prepared.schema);
        setFormNameClass(formNameToCssClass(prepared.formName || formName));
        onFormSchemaLoad?.(prepared.schema.components);
        setError(null);
        setLoading(false);
      } catch (err) {
        console.error("❌ Error fetching schema:", err);
        if (isMounted) {
          setError("Failed to load form structure.");
          setLoading(false);
        }
      }
    };

    loadSchema();
    return () => {
      isMounted = false;
    };
    // initialSchema read once per mount (parent remounts on tab/formId change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanFormId, API_URL]);

  /* ───────── Proper Submission Injection ───────── */
  // Initialise from props so the first Form paint already has server data (esp. accreditation photo).
  // Previously { data: null } until useEffect ran — Form.io often kept a stale file preview.
  const [capturedSubmission, setCapturedSubmission] = useState(() => ({
    formId: cleanFormId || null,
    data: (() => {
      if (!submission?.data) return {};
      let d = normalizeDataGridForFormioRender(
        JSON.parse(JSON.stringify(submission.data))
      );
      return d;
    })(),
  }));
  /** Avoid stale submission: re-capture when participant, form, or version changes. */
  const lastCaptureRef = useRef({
    formId: cleanFormId || null,
    submissionVersion,
    participantId: participantId || null,
  });

  // Capture submission when participant/tab changes or parent bumps submissionVersion (e.g. after refetch).
  // Avoid recapturing on every parent render to prevent form reset loops.
  useEffect(() => {
    if (!cleanFormId) return;
    const prev = lastCaptureRef.current;
    const pid = participantId || null;
    if (
      prev.formId === cleanFormId &&
      prev.submissionVersion === submissionVersion &&
      prev.participantId === pid
    ) {
      return;
    }
    lastCaptureRef.current = { formId: cleanFormId, submissionVersion, participantId: pid };
    lastCaddieSubmitPayloadRef.current = null;
    let data = submission?.data
      ? normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(submission.data)))
      : {};
    sanitizeSelectSubmissionValues(data);
    if (hasCaddieSelectRef.current) {
      const merged = mergeKnownCaddiesIntoOptionsList(
        caddiesListRef.current,
        data
      );
      caddiesListRef.current = merged;
      caddiesByIdRef.current = buildCaddiesById(merged);
      if (caddiesByIdRef.current.size) {
        data = prefillCaddieDatagridRows(data, caddiesByIdRef.current);
      }
    }
    setCapturedSubmission({ formId: cleanFormId, data });
    lastEmittedDataRef.current = JSON.stringify(data);

    if (hasCaddieSelectRef.current && caddiesListRef.current.length) {
      setFormSchema((prev) => {
        if (!prev?.components) return prev;
        const next = JSON.parse(JSON.stringify(prev));
        injectCaddieSelectValues(
          next.components,
          caddiesListRef.current,
          data
        );
        return next;
      });
    }
    // submission is read when formId / submissionVersion change — do not list submission in deps
    // or the form would reset on unrelated parent re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanFormId, submissionVersion, participantId]);

  /** After schema + caddie options load, restore dataGrid.knownCaddies → rows (layout was null on first capture). */
  useEffect(() => {
    if (!formSchema?.components?.length || !hasCaddieSelectRef.current) return;
    if (!submission?.data || typeof submission.data !== "object") return;

    let data = normalizeDataGridForFormioRender(
      JSON.parse(JSON.stringify(submission.data))
    );
    if (caddiesByIdRef.current.size) {
      data = prefillCaddieDatagridRows(data, caddiesByIdRef.current);
    }

    const dataJson = JSON.stringify(data);
    setCapturedSubmission((prev) => {
      if (prev.formId === cleanFormId && JSON.stringify(prev.data) === dataJson) {
        return prev;
      }
      return { formId: cleanFormId, data };
    });
    lastEmittedDataRef.current = dataJson;
    if (liveDataRef) {
      try {
        liveDataRef.current = JSON.parse(dataJson);
      } catch {
        liveDataRef.current = { ...data };
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSchema, submissionVersion, participantId, cleanFormId]);

  useLayoutEffect(() => {
    try {
      lastEmittedDataRef.current = JSON.stringify(capturedSubmission.data || {});
    } catch {
      lastEmittedDataRef.current = "";
    }
  }, [capturedSubmission.data]);

  useEffect(() => {
    if (!liveDataRef) return;
    const d = capturedSubmission.data;
    if (d && typeof d === "object") {
      try {
        liveDataRef.current = JSON.parse(JSON.stringify(d));
      } catch {
        liveDataRef.current = { ...d };
      }
    } else {
      liveDataRef.current = {};
    }
  }, [liveDataRef, capturedSubmission.formId, capturedSubmission.data]);

  const flushLiveDataFromWebform = useCallback(() => {
    if (!liveDataRef) return;
    const fi = reactFormRef.current?.formio;
    if (!fi) return;
    liveDataRef.current = snapshotFormioInstanceData(fi);
  }, [liveDataRef]);

  useEffect(() => {
    if (!flushLiveFormDataRef) return;
    flushLiveFormDataRef.current = flushLiveDataFromWebform;
    return () => {
      flushLiveFormDataRef.current = () => {};
    };
  }, [flushLiveFormDataRef, flushLiveDataFromWebform]);

  useEffect(() => {
    if (!clearFormErrorsRef) return;
    clearFormErrorsRef.current = () => {
      clearFormioValidationState(reactFormRef.current?.formio);
    };
    return () => {
      clearFormErrorsRef.current = () => {};
    };
  }, [clearFormErrorsRef]);

  useEffect(() => {
    if (!triggerSaveRef) return;

    const runTabSwitchSave = async (resolve) => {
      const fi = reactFormRef.current?.formio;
      if (!fi) {
        resolve(true);
        return;
      }

      try {
        let data = snapshotFormioInstanceData(fi);
        if (hasCaddieSelectRef.current && caddiesByIdRef.current.size) {
          data = resolveCaddieSubmitPayload(
            data,
            caddiesByIdRef.current,
            fi,
            lastCaddieSubmitPayloadRef
          );
          if (liveDataRef) {
            try {
              liveDataRef.current = JSON.parse(JSON.stringify(data));
            } catch {
              liveDataRef.current = { ...data };
            }
          }
          lastEmittedDataRef.current = JSON.stringify(data);
        }
        let ok = true;
        if (typeof onFormSave === "function") {
          const result = await onFormSave(data, { viaTabSwitch: true });
          ok = result !== false && result !== null;
        }
        resolve(ok);
      } catch (err) {
        console.warn("Tab switch save failed:", err);
        resolve(false);
      } finally {
        saveViaTabSwitchRef.current = false;
        pendingSaveResolveRef.current = null;
      }
    };

    triggerSaveRef.current = () =>
      new Promise((resolve) => {
        flushLiveDataFromWebform();
        saveViaTabSwitchRef.current = true;
        runTabSwitchSave(resolve);
      });

    return () => {
      triggerSaveRef.current = () => Promise.resolve(true);
      pendingSaveResolveRef.current = null;
    };
  }, [
    triggerSaveRef,
    flushLiveDataFromWebform,
    onValidationErrors,
    onFormSave,
  ]);

  useEffect(() => {
    if (!triggerFormSubmitRef) return;
    triggerFormSubmitRef.current = async () => {
      flushLiveDataFromWebform();
      saveViaTabSwitchRef.current = false;
      const fi =
        reactFormRef.current?.formio ||
        formioWebformRef?.current ||
        null;
      if (!fi || typeof fi.submit !== "function") {
        if (typeof onValidationErrors === "function") onValidationErrors([]);
        return;
      }

      /* Force mark invalid fields before submit — footer Save often skips Form.io UI marks. */
      const isValid = forceFormioShowValidationErrors(fi);
      if (!isValid) {
        const items = collectFormioValidationItems(fi);
        if (typeof onValidationErrors === "function") onValidationErrors(items);
        return;
      }

      try {
        await Promise.resolve(fi.submit());
      } catch (err) {
        console.warn("Form.io submit rejected (validation errors):", err);
        forceFormioShowValidationErrors(fi);
        const items = collectFormioValidationItems(fi, err);
        if (typeof onValidationErrors === "function") {
          onValidationErrors(items);
        }
      }
    };
    return () => {
      triggerFormSubmitRef.current = () => Promise.resolve();
    };
  }, [triggerFormSubmitRef, flushLiveDataFromWebform, onValidationErrors, formioWebformRef]);

  const mergedOptions = useMemo(
    () => (formOptions ? { ...USER_PORTAL_FORMIO_OPTIONS, ...formOptions } : USER_PORTAL_FORMIO_OPTIONS),
    [formOptions]
  );

  const memoizedSubmission = useMemo(() => {
    const base = { ...(capturedSubmission.data || {}) };
    sanitizeSelectSubmissionValues(base);
    if (!formSchema?.components) return { data: base };

    const formKeys = collectFormDataKeys(formSchema.components);
    const data = { ...base };
    if (formKeys.has("Group_id") && data.Group_id == null) data.Group_id = "";
    if (formKeys.has("groupName") && data.groupName == null) data.groupName = "";
    return { data };
  }, [capturedSubmission.data, formSchema]);

  /* ───────── Form handlers (same pattern as UserFormView) ───────── */
  const handleFormChange = useCallback(
    (newSub) => {
      try {
        if (newSub?.data && liveDataRef) {
          try {
            liveDataRef.current = JSON.parse(JSON.stringify(newSub.data));
          } catch {
            liveDataRef.current = { ...newSub.data };
          }
        }

        if (!newSub?.data || !newSub.changed) return;

        const cleanData = JSON.parse(JSON.stringify(newSub.data));

        if (hasCaddieSelectRef.current && newSub.changed) {
          const formioInstance =
            reactFormRef.current?.formio ||
            formioWebformRef?.current ||
            null;
          const synced = applyCaddieOnFormChange(
            newSub,
            formioInstance,
            caddiesByIdRef.current
          );
          const syncedJson = JSON.stringify(synced);
          const radioChange = isDefaultRadioFormChange(newSub.changed);
          if (radioChange) {
            lastCaddieSubmitPayloadRef.current = finalizeCaddieGridForSubmit(
              synced,
              caddiesByIdRef.current,
              formioInstance
            );
          }
          if (radioChange || syncedJson !== JSON.stringify(cleanData)) {
            // Ensure live email field updates even when React remount is deferred.
            if (formioInstance && !radioChange) {
              pushFormioSubmissionData(formioInstance, synced, { gridOnly: true });
            }
            setCapturedSubmission((prev) => ({
              formId: prev.formId,
              data: synced,
            }));
            if (liveDataRef) {
              try {
                liveDataRef.current = JSON.parse(JSON.stringify(synced));
              } catch {
                liveDataRef.current = { ...synced };
              }
            }
            lastEmittedDataRef.current = syncedJson;
            onDataChange?.({ data: synced });
            paintProgressBadges(formioInstance, synced);
            return;
          }
        }

        const serialized = JSON.stringify(cleanData);
        if (serialized === lastEmittedDataRef.current) return;
        lastEmittedDataRef.current = serialized;

        const stripLargeBase64 = (obj) => {
          for (const key in obj) {
            if (
              typeof obj[key] === "string" &&
              obj[key].startsWith("data:image") &&
              obj[key].length > 1000
            ) {
              obj[key] = "base64_removed_to_save_space";
            } else if (typeof obj[key] === "object" && obj[key] !== null) {
              stripLargeBase64(obj[key]);
            }
          }
        };

        stripLargeBase64(cleanData);
        onDataChange?.({ data: cleanData });
        paintProgressBadges(
          reactFormRef.current?.formio || formioWebformRef?.current || null,
          cleanData
        );
      } catch (err) {
        console.error("handleFormChange error:", err);
      }
    },
    [liveDataRef, onDataChange, formioWebformRef, paintProgressBadges]
  );

  const handleFormReady = useCallback(
    (formio) => {
      try {
        if (!formio) return;
        // Local API save only — never POST to Form.io cloud.
        formio.nosubmit = true;
        if (formioWebformRef) formioWebformRef.current = formio;
        if (reactFormRef.current) {
          reactFormRef.current.formio = formio;
        } else {
          reactFormRef.current = { formio };
        }

        // If the form has a pre-set invitationTypes value, force calculated values to re-run
        // after all components have finished initialising their options.
        const presetInvType = formio.submission?.data?.invitationTypes;
        if (presetInvType) {
          setTimeout(() => {
            try {
              if (formio.submission) {
                formio.setValue(formio.submission, { noValidate: true, noEmit: true });
              }
            } catch (e) {
              // silent — best-effort recalculation
            }
          }, 200);
        }

        if (hasCaddieSelectRef.current) {
          let base =
            formio.submission?.data ||
            capturedSubmission.data ||
            liveDataRef?.current ||
            {};
          try {
            base = normalizeDataGridForFormioRender(
              JSON.parse(JSON.stringify(base))
            );
          } catch {
            base = normalizeDataGridForFormioRender({ ...base });
          }
          // Always reinject so URL dataSrc cannot hang the open dropdown.
          reinjectCaddieOptionsOnFormio(
            formio,
            Array.from(caddiesByIdRef.current.values()),
            base
          );
          attachCaddieEmailAutoFill(formio, () => caddiesByIdRef.current);
          if (caddiesByIdRef.current.size) {
            const synced = syncCaddieFieldsForFormio(
              formio,
              base,
              caddiesByIdRef.current
            );
            setCapturedSubmission((prev) => ({
              formId: prev.formId,
              data: synced,
            }));
            if (liveDataRef) {
              try {
                liveDataRef.current = JSON.parse(JSON.stringify(synced));
              } catch {
                liveDataRef.current = { ...synced };
              }
            }
            paintProgressBadges(formio, synced);
          } else {
            paintProgressBadges(formio, base);
          }
        } else {
          const readyData =
            formio.submission?.data ||
            capturedSubmission.data ||
            liveDataRef?.current ||
            {};
          paintProgressBadges(formio, readyData);
        }
      } catch (err) {
        console.error("handleFormReady error:", err);
      }
    },
    [capturedSubmission.data, liveDataRef, formioWebformRef, paintProgressBadges]
  );

  const handleFormSubmit = useCallback(
    async (submission) => {
      const viaTabSwitch = saveViaTabSwitchRef.current;
      let ok = true;
      let data = submission?.data || {};
      try {
        let fi = reactFormRef.current?.formio;
        data = fi
          ? snapshotFormioInstanceData(fi)
          : submission?.data || {};
        if (!fi) {
          fi = reactFormRef.current?.formio;
          if (fi) data = snapshotFormioInstanceData(fi);
        }
        if (hasCaddieSelectRef.current && caddiesByIdRef.current.size) {
          data = resolveCaddieSubmitPayload(
            data,
            caddiesByIdRef.current,
            fi,
            lastCaddieSubmitPayloadRef
          );
          if (liveDataRef) {
            try {
              liveDataRef.current = JSON.parse(JSON.stringify(data));
            } catch {
              liveDataRef.current = { ...data };
            }
          }
          lastEmittedDataRef.current = JSON.stringify(data);
        }
        if (typeof onFormSave === "function") {
          const saveResult = await onFormSave(data, { viaTabSwitch });
          if (saveResult === null) {
            return submission;
          }
          ok = saveResult !== false;
        }
      } catch (err) {
        console.error("FormioTabRenderer handleFormSubmit error:", err);
        ok = false;
      }
      if (ok && hasCaddieSelectRef.current && data && typeof data === "object") {
        setCapturedSubmission({ formId: cleanFormId, data });
      }
      if (pendingSaveResolveRef.current) {
        saveViaTabSwitchRef.current = false;
        pendingSaveResolveRef.current(ok);
        pendingSaveResolveRef.current = null;
      } else if (!viaTabSwitch) {
        saveViaTabSwitchRef.current = false;
      }
      if (hasCaddieSelectRef.current && ok && data && typeof data === "object") {
        return { ...submission, data };
      }
      return submission;
    },
    [onFormSave, cleanFormId, liveDataRef]
  );

  /** Collect errors for the page summary — do NOT re-call force here (recursion with showErrors). */
  const handleFormError = useCallback(
    (errors) => {
      console.error("Form.io onError:", errors);
      const fi =
        reactFormRef.current?.formio || formioWebformRef?.current || null;
      const items = collectFormioValidationItems(fi, errors);
      if (items.length > 0) {
        pendingErrorItemsRef.current = [
          ...pendingErrorItemsRef.current,
          ...items.filter(
            (it) => !pendingErrorItemsRef.current.some((e) => e.text === it.text)
          ),
        ];
      }
      clearTimeout(errorDebounceRef.current);
      errorDebounceRef.current = setTimeout(() => {
        const merged = pendingErrorItemsRef.current;
        pendingErrorItemsRef.current = [];
        if (typeof onValidationErrors === "function") {
          onValidationErrors(merged);
        }
      }, 80);
    },
    [onValidationErrors, formioWebformRef]
  );

  useEffect(() => {
    return () => clearTimeout(errorDebounceRef.current);
  }, []);

  /* ───────── Render ───────── */
  if (loading)
    return (
      <div className="guest-form-skeleton" aria-busy="true" aria-label="Loading form">
        {Array.from({ length: skeletonPanels }).map((_, panel) => (
          <div key={panel} className="guest-form-skeleton__panel">
            <div className="guest-form-skeleton__panel-header">
              <div className="guest-form-skeleton__icon" />
              <div className="guest-form-skeleton__title" />
            </div>
            <div className="guest-form-skeleton__body">
              {[0, 1, 2].map((row) => (
                <div key={row} className="guest-form-skeleton__row">
                  <div className="guest-form-skeleton__field">
                    <div className="guest-form-skeleton__label" />
                    <div className="guest-form-skeleton__input" />
                  </div>
                  <div className="guest-form-skeleton__field">
                    <div className="guest-form-skeleton__label" style={{ width: row === 1 ? '45%' : '60%' }} />
                    <div className="guest-form-skeleton__input" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );

  if (error)
    return <div className="p-3 text-center text-danger">{error}</div>;

  if (!formSchema)
    return <div className="p-3 text-center text-muted">No form schema found.</div>;

  /* Include submissionVersion so file / image fields remount after server refetch (e.g. cropped photo). */
  const formKey = `formio-${participantId || 'new'}-${cleanFormId}-v${submissionVersion}`;

  return (
    <div
      className={`formio-tab-container user-layout user-form-prototype${
        showInlineErrors ? " user-form--show-errors" : ""
      }${formNameClass ? ` ${formNameClass}` : ""}${
        isInvitationTypesForm ? " invitation-types-form" : ""
      }`}
    >
      <Form
        ref={reactFormRef}
        key={formKey}
        form={formSchema}
        submission={memoizedSubmission}
        options={mergedOptions}
        onChange={handleFormChange}
        onSubmit={handleFormSubmit}
        onReady={handleFormReady}
        onInitialized={handleFormReady}
        onError={handleFormError}
      />
    </div>
  );
};

export default FormioTabRenderer;